const React = require('react');
const T = require('prop-types');
const { default: Styled } = require('styled-components');

const { default: Tooltip } = require('@mui/material/Tooltip');

const { getSchoolTileImageSrc } = require('utils/image');
const { transient$Props } = require('utils/styles');
const { DEFAULT_SCHOOL_TILE_SIZE, SCHOOL_TILE_BORDER_RADIUS } = require('utils/constants');
const { nearpeerTheme: NearpeerTheme } = require('styles/theme');
const UnreadMessagesBadge = require('routes/app/components/UnreadMessagesBadge');

const internals = {};

module.exports = function SchoolTile(props) {

    const {
        schools,
        isCurrentSchool,
        slug,
        useNearpeerTheme = false,
        borderWidth = 2,
        showTooltip,
        tileSize = DEFAULT_SCHOOL_TILE_SIZE,
        unreadCount,
        onClick,
        ...rest
    } = props;

    const {
        StyledSchoolTile,
        StyledUnreadMessagesBadge
    } = internals;

    const tileSchool = schools.find((school) => school.slug === slug);

    const imgSrc = getSchoolTileImageSrc(tileSchool || { slug });

    const interactiveProps = onClick ? {
        tabIndex: 0,
        role: 'button',
        onClick,
        onKeyDown: (e) => {

            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(e);
            }
        }
    } : {};

    const tileProps = {
        $isCurrentSchool: isCurrentSchool,
        $useNearpeerTheme: useNearpeerTheme,
        $borderWidth: borderWidth,
        $tileSize: tileSize,
        ...interactiveProps,
        ...rest
    };

    if (!showTooltip) {
        return (
            <StyledSchoolTile {...tileProps}>
                <img
                    alt={tileSchool?.name}
                    src={imgSrc}
                />
                {Number(unreadCount) > 0 && (
                    <StyledUnreadMessagesBadge numUnreads={unreadCount} $useNearpeerTheme={useNearpeerTheme} />
                )}
            </StyledSchoolTile>
        );
    }

    return (
        <Tooltip
            arrow={true}
            title={tileSchool?.name}
            placement='right'
            componentsProps={{
                tooltip: {
                    sx: {
                        bgcolor: 'rgba(0, 0, 0, 0.9)',
                        color: '#fff'
                    }
                },
                arrow: {
                    sx: {
                        color: 'rgba(0, 0, 0, 0.9)'
                    }
                }
            }}
        >
            <StyledSchoolTile {...tileProps}>
                <img
                    alt={tileSchool?.name}
                    src={imgSrc}
                />
                {Number(unreadCount) > 0 && (
                    <StyledUnreadMessagesBadge numUnreads={unreadCount} $useNearpeerTheme={useNearpeerTheme} />
                )}
            </StyledSchoolTile>
        </Tooltip>
    );
};

module.exports.propTypes = {
    schools: T.arrayOf(T.object).isRequired,
    isCurrentSchool: T.bool.isRequired,
    slug: T.string.isRequired,
    showDetails: T.bool,
    useNearpeerTheme: T.bool,
    borderWidth: T.number,
    showTooltip: T.bool,
    tileSize: T.number,
    unreadCount: T.number,
    onClick: T.func
};

internals.StyledSchoolTile = Styled('div', transient$Props)`

    ${({ $tileSize }) => {

        return `
            min-width: ${$tileSize}px;
            min-height: ${$tileSize}px;
            max-width: ${$tileSize}px;
            max-height: ${$tileSize}px;
        `;
    }}

    position: relative;
    background: white;
    cursor: pointer;

    border-radius: ${SCHOOL_TILE_BORDER_RADIUS}px;

    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: center;

    img {
        width: 100%;
        height: 100%;
        padding: 5px;
    }

    ${({ theme, $useNearpeerTheme, $isCurrentSchool, $borderWidth }) => {

        if ($useNearpeerTheme) {
            return `
                border: ${$borderWidth}px solid ${$isCurrentSchool ? NearpeerTheme.palette.secondary.main : '#AD97D7'};

                &:hover {
                    border: ${$borderWidth}px solid ${NearpeerTheme.palette.secondary.main};
                }

                &:focus-visible {
                    outline: 2px solid ${NearpeerTheme.palette.secondary.main} !important;
                    outline-offset: 2px;
                }
            `;
        }

        return `
            border: ${$borderWidth}px solid ${$isCurrentSchool ? theme.palette.secondary.main : theme.palette.grey[400]};

            &:hover {
                border: ${$borderWidth}px solid ${theme.palette.secondary.main};
            }

            &:focus-visible {
                outline: 2px solid ${theme.palette.secondary.main} !important;
                outline-offset: 2px;
            }
        `;
    }}
`;

internals.StyledUnreadMessagesBadge = Styled(UnreadMessagesBadge, transient$Props)`
    position: absolute;
    top: 0px;
    right: 0px;

    span {
        border-radius: 13px !important;
    }

    ${({ $useNearpeerTheme }) => {

        if (!$useNearpeerTheme) {
            return '';
        }

        return `
            .MuiBadge-badge {
                background-color: ${NearpeerTheme.palette.secondary.main};
                color: white;
            }
        `;
    }}
`;
